import * as TabsPrimitive from '@radix-ui/react-tabs';
import { Plus, type LucideIcon } from 'lucide-react';
import { cn } from './lib/cn';
import { Badge } from './Badge';

export interface TabItem {
  value: string;
  label: string;
  icon?: LucideIcon;
  count?: number;
  disabled?: boolean;
}

export interface TabsProps {
  items: readonly TabItem[];
  value: string;
  onValueChange: (value: string) => void;
  /**
   * `underline` — channel header (Posts / Files / Wiki …), 2px purple rule.
   * `pill`      — composer modes (Post / Announcement / Poll / Praise).
   * `segment`   — compact grouped control.
   */
  variant?: 'underline' | 'pill' | 'segment';
  /** Renders a trailing "+" affordance (add a tab / add a mode). */
  onAdd?: () => void;
  addLabel?: string;
  className?: string;
}

const LIST = {
  underline: 'flex items-center gap-1 border-b border-border',
  pill: 'flex items-center gap-1',
  segment: 'inline-flex items-center gap-0.5 rounded-lg bg-secondary p-0.5',
} as const;

const TRIGGER = {
  underline:
    'relative -mb-px flex items-center gap-1.5 border-b-2 border-transparent px-3 py-2.5 text-md font-medium text-muted-foreground transition-colors duration-fast hover:text-foreground data-[state=active]:border-primary data-[state=active]:font-semibold data-[state=active]:text-primary',
  pill: 'flex items-center gap-1.5 rounded-lg px-3 py-2 text-md font-medium text-secondary-foreground transition-colors duration-fast hover:bg-subtle data-[state=active]:bg-primary-subtle data-[state=active]:font-semibold data-[state=active]:text-primary',
  segment:
    'flex items-center gap-1.5 rounded-md px-3 py-1.5 text-base font-medium text-muted-foreground transition-colors duration-fast data-[state=active]:bg-card data-[state=active]:text-foreground data-[state=active]:shadow-xs',
} as const;

/** Tab bar. Panels are rendered by the caller — this is the control only. */
export function Tabs({
  items, value, onValueChange, variant = 'underline', onAdd, addLabel = 'Add tab', className,
}: TabsProps) {
  return (
    <TabsPrimitive.Root value={value} onValueChange={onValueChange}>
      <TabsPrimitive.List className={cn(LIST[variant], className)}>
        {items.map((item) => {
          const Icon = item.icon;
          return (
            <TabsPrimitive.Trigger
              key={item.value}
              value={item.value}
              disabled={item.disabled}
              className={cn(TRIGGER[variant], 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring')}
            >
              {Icon ? <Icon size={16} strokeWidth={2} aria-hidden /> : null}
              {item.label}
              {item.count !== undefined ? (
                <Badge count={item.count} tone="neutral" size="sm" />
              ) : null}
            </TabsPrimitive.Trigger>
          );
        })}

        {onAdd ? (
          <button
            type="button"
            onClick={onAdd}
            aria-label={addLabel}
            title={addLabel}
            className="ml-1 inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground transition-colors duration-fast hover:bg-subtle hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <Plus size={16} strokeWidth={2.25} aria-hidden />
          </button>
        ) : null}
      </TabsPrimitive.List>
    </TabsPrimitive.Root>
  );
}
